// Drawola Studio: full-text search over the saved draws. Fork-only.
//
// Pure functions, no React: the Studio only decides how to paint the result.

import type { DocListEntry } from "../storage/multiFileStore";

/** Roughly how many characters of context a content snippet carries. */
const SNIPPET_LENGTH = 80;

export type DocMatch = {
  doc: DocListEntry;
  /** Present only when the match came from content, not from the name. */
  snippet?: { text: string; hitStart: number; hitEnd: number };
};

/**
 * Filter the saved draws by a search box query.
 *
 * A draw matches when the query is a substring of its name or of any string
 * harvested from its scene (see `../storage/searchText`). Matching is
 * case-insensitive and the incoming order is preserved: the Studio applies its
 * own sort afterwards.
 *
 * A name match carries no snippet, because the name is already on the card. A
 * content match carries a window of the first matching string around its first
 * hit, so the card can show WHY the draw is in the list.
 *
 * @param docs  the list as `listDocs()` returned it
 * @param query raw search box value; empty or blank keeps every doc
 */
export function filterDocs(docs: DocListEntry[], query: string): DocMatch[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return docs.map((doc) => ({ doc }));
  }

  const matches: DocMatch[] = [];
  for (const doc of docs) {
    if (doc.name.toLowerCase().includes(needle)) {
      matches.push({ doc });
      continue;
    }
    const hit = findFirstHit(doc.searchStrings, needle);
    if (hit) {
      matches.push({
        doc,
        snippet: buildSnippet(hit.text, hit.index, needle.length),
      });
    }
  }
  return matches;
}

/** First string containing `needle`, with the offset of its first occurrence. */
function findFirstHit(
  strings: readonly string[],
  needle: string,
): { text: string; index: number } | null {
  for (const text of strings) {
    const index = text.toLowerCase().indexOf(needle);
    if (index !== -1) {
      return { text, index };
    }
  }
  return null;
}

/**
 * Cut a ~`SNIPPET_LENGTH` window out of `source`, centered on the hit.
 *
 * The returned offsets index into `snippet.text`, not into `source`: the caller
 * slices the snippet with them to wrap the hit in a highlight.
 */
function buildSnippet(
  source: string,
  hitIndex: number,
  hitLength: number,
): { text: string; hitStart: number; hitEnd: number } {
  if (source.length <= SNIPPET_LENGTH) {
    return { text: source, hitStart: hitIndex, hitEnd: hitIndex + hitLength };
  }

  // A hit longer than the window still gets its own room, so the highlight is
  // never cut off mid-way.
  const windowLength = Math.max(SNIPPET_LENGTH, hitLength);
  const contextBefore = Math.floor(Math.max(0, windowLength - hitLength) / 2);
  const start = Math.max(
    0,
    Math.min(hitIndex - contextBefore, source.length - windowLength),
  );
  const end = Math.min(source.length, start + windowLength);

  return {
    text: source.slice(start, end),
    hitStart: hitIndex - start,
    hitEnd: Math.min(hitIndex + hitLength, end) - start,
  };
}
